import { SharePageModal } from '@app/components/pagesList/SharePageModal';
import { Button } from '@app/components/various/Button';
import { ChevronLeft, Link } from '@app/components/various/Icons';
import { TextEllipsis } from '@app/components/various/TextEllipsis';
import { Tooltip } from '@app/components/various/Tooltip';
import { createEditorWindowName } from '@app/logic/cms/preview';
import { getIsPublished, getSharePageData } from '@app/logic/pages';
import { composeEditorPreviewPath } from '@app/utils/composeEditorPreviewPath';
import { isDefined } from '@app/utils/is';
import { Cms } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EditorLanguageContext } from '../context/EditorLanguageContext';
import { EditorWidthSwitcher } from '../EditorWidthSwitcher';
import { PageStatus } from '../PageStatus';
import { SegmentationSwitcher } from '../segmentationSwitcher';

interface Props {
  pageMeta: Cms.Page;
  currentSegmentation: {
    marketId: Nullable<string>;
    pricelistId: Nullable<string>;
  };
  segmentationChange: (marketId: string, pricelistId: string) => void;
}

export const PreviewTopBar = ({ pageMeta, currentSegmentation, segmentationChange }: Props) => {
  const { editorLanguage } = React.useContext(EditorLanguageContext);
  const { t } = useTranslation(['cms', 'common']);
  const { markets, name, id, status, languages } = pageMeta;
  const isPublished = getIsPublished(status);

  const [isShareModalOpen, setShareModalOpen] = React.useState(false);

  const handleOpenEditor = React.useCallback(() => {
    const urlToOpen = composeEditorPreviewPath({ lang: editorLanguage, pageId: id, pathType: 'editor' });

    const windowName = createEditorWindowName(id);
    const windowRef = window.open('', windowName);
    const isUrlOfWindowRefSame = `${windowRef?.location.pathname}${windowRef?.location.search}` === urlToOpen;

    if (windowRef && isUrlOfWindowRefSame) {
      return windowRef.focus();
    }

    window.open(urlToOpen, windowName);
  }, [id, editorLanguage]);

  const handleHideShareModal = React.useCallback(() => {
    setShareModalOpen(false);
  }, []);

  const showShareModal = React.useCallback(() => {
    setShareModalOpen(true);
  }, []);

  const pageToShare = getSharePageData(pageMeta);

  return (
    <>
      <div className="sticky top-0 z-topBar flex min-h-18 w-full shrink-0 items-center border border-transparent border-b-neutral-80 bg-white px-4 shadow-2">
        <div className="flex flex-1 items-center gap-3">
          <Button icon={ChevronLeft} color="secondary" variant="ghost" onClick={handleOpenEditor}>
            {t('common:back')}
          </Button>

          <div className="flex flex-col gap-1">
            <PageStatus status="preview" />
            <Tooltip content={name} placement="bottom-center">
              <TextEllipsis lines={2} noWrapText={false} className="body-1">
                {name}
              </TextEllipsis>
            </Tooltip>
          </div>

          <SegmentationSwitcher
            markets={markets}
            currentSegmentation={currentSegmentation}
            segmentationChange={segmentationChange}
            languages={languages}
          />
        </div>

        <EditorWidthSwitcher />

        <div className="flex flex-1 items-center justify-end gap-4">
          {isPublished && (
            <Tooltip content={t('cms:get_shareable_link')}>
              <Button icon={Link} color="secondary" onClick={showShareModal} />
            </Tooltip>
          )}
        </div>
      </div>

      {isDefined(pageToShare) && <SharePageModal isOpen={isShareModalOpen} onClose={handleHideShareModal} {...pageToShare} />}
    </>
  );
};
